/**
 * SALES-FORECASTING MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

// SeedYearModal.jsx — backlog 141. Chooses what a "Seed year" run reads from and
// how it writes, and shows the plan BEFORE it runs: this writes hundreds of
// cells at once, so "what is about to happen" has to be on screen next to the
// button that does it.

import React, { useMemo, useState, useEffect, useRef } from "react";
import { planSeed, describePlan } from "../utils/seedForecast.js";
import "./SeedYearModal.css";

export default function SeedYearModal({
  open,
  onClose,
  onConfirm,              // (plan, opts) => Promise<void>
  targetYear,
  hasActuals = false,
  rawForecast = [],
  rawActuals = [],
  state,
  distributor = "",
  distributorCount = 0,
  skus = [],
}) {
  const [source, setSource]       = useState(hasActuals ? "actuals" : "forecast");
  const [sourceYear, setSrcYear]  = useState("");
  const [growthPct, setGrowth]    = useState("0");
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy]           = useState(false);
  const closeRef = useRef(null);

  // Offer only years that ACTUALLY HOLD NUMBERS for the chosen source, in this
  // state. The dashboard's own `yearOptions` is the wrong list here: it is
  // "current year and the next two, plus any year with a forecast", so on a new
  // tenant it contains no past year at all — and this feature exists precisely
  // to seed FROM the past. Driving the picker off the data means the empty
  // selection is unreachable instead of being the default. Found by opening the
  // dialog on the demo tenant, where it defaulted to an empty 2027.
  const sourceYearOptions = useMemo(() => {
    const rows = source === "actuals" ? rawActuals : rawForecast;
    const key  = source === "actuals" ? "CaseActual" : "CaseForecast";
    const years = new Set();
    for (const r of rows) {
      const f = r?.fields;
      if (!f || !f.Year) continue;
      if (f.State !== state) continue;
      if (distributor && f.Distributor !== distributor) continue;
      if (!(Number(f[key]) > 0)) continue;
      if (String(f.Year) === String(targetYear)) continue;
      years.add(String(f.Year));
    }
    return [...years].sort();
  }, [source, rawForecast, rawActuals, state, distributor, targetYear]);

  // Default to the nearest year BELOW the target — "last year", the question
  // this feature answers. Only reach forward if nothing earlier exists.
  useEffect(() => {
    if (!open) return;
    const below = sourceYearOptions.filter((y) => Number(y) < Number(targetYear));
    setSrcYear(below.length ? below[below.length - 1] : (sourceYearOptions[0] ?? ""));
  }, [open, targetYear, sourceYearOptions]);

  useEffect(() => {
    if (!open) return;
    setSource(hasActuals ? "actuals" : "forecast");
    setGrowth("0");
    setOverwrite(false);
    setBusy(false);
  }, [open, hasActuals]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    document.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  const plan = useMemo(() => {
    if (!open || !sourceYear) return null;
    return planSeed({
      source, rawForecast, rawActuals,
      sourceYear, targetYear, state, distributor, distributorCount, skus,
      growthPct: Number(growthPct) || 0,
      overwrite,
    });
  }, [open, source, rawForecast, rawActuals, sourceYear, targetYear, state, distributor, distributorCount, skus, growthPct, overwrite]);

  if (!open) return null;

  const canRun = !!plan && plan.ops.length > 0 && !busy;
  const growthNum = Number(growthPct);
  const growthBad = growthPct !== "" && (!Number.isFinite(growthNum) || growthNum < -100 || growthNum > 1000);

  return (
    <div className="sym-overlay" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="sym-card fc-module" role="dialog" aria-modal="true" aria-labelledby="sym-title">
        <div className="sym-head">
          <h3 className="sym-title" id="sym-title">Seed {targetYear} from…</h3>
          <button ref={closeRef} className="sym-x" onClick={onClose} disabled={busy} aria-label="Close">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <p className="sym-scope">
          {state}{distributor ? ` · ${distributor}` : ""} · {skus.length} SKU{skus.length === 1 ? "" : "s"} in view
        </p>

        <fieldset className="sym-field">
          <legend className="sym-legend">Source</legend>
          <label className={`sym-radio${!hasActuals ? " sym-radio-off" : ""}`}>
            <input
              type="radio" name="sym-source" value="actuals"
              checked={source === "actuals"}
              disabled={!hasActuals}
              onChange={() => setSource("actuals")}
            />
            <span>
              Actual depletions
              {!hasActuals && <em className="sym-note"> — none loaded for this workspace</em>}
            </span>
          </label>
          <label className="sym-radio">
            <input
              type="radio" name="sym-source" value="forecast"
              checked={source === "forecast"}
              onChange={() => setSource("forecast")}
            />
            <span>A previous forecast</span>
          </label>
        </fieldset>

        <div className="sym-row">
          <label className="sym-field">
            <span className="sym-legend">Read from year</span>
            <select
              className="selector-select"
              value={sourceYear}
              onChange={(e) => setSrcYear(e.target.value)}
              disabled={sourceYearOptions.length === 0}
            >
              {sourceYearOptions.length === 0
                ? <option value="">No {source === "actuals" ? "depletions" : "forecasts"} for {state}</option>
                : sourceYearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>

          <label className="sym-field">
            <span className="sym-legend">Growth %</span>
            <input
              className="sym-input"
              type="number" step="1" min="-100" max="1000"
              value={growthPct}
              onChange={(e) => setGrowth(e.target.value)}
              aria-invalid={growthBad || undefined}
            />
          </label>
        </div>

        <label className="sym-check">
          <input
            type="checkbox"
            checked={overwrite}
            onChange={(e) => setOverwrite(e.target.checked)}
          />
          <span>Overwrite cells that already have a value</span>
        </label>

        {/* Never render an empty plan line. With no usable source year `plan` is
            null and describePlan returns "", which left a blank orange band
            saying nothing — the one state where the user most needs a sentence. */}
        <p className={`sym-plan${!canRun ? " sym-plan-empty" : ""}`}>
          {growthBad
            ? "Growth must be between -100 and 1000."
            : !sourceYear
              ? `No ${source === "actuals" ? "depletions" : "other forecast years"} to read from for ${state}. ` +
                `${source === "actuals" ? "Import depletions" : "Try the other source"}, or type the first year by hand.`
              : describePlan(plan)}
        </p>

        <div className="sym-actions">
          <button className="sym-cancel" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            className="sym-go"
            disabled={!canRun || growthBad}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm(plan, { source, sourceYear, growthPct: Number(growthPct) || 0, overwrite });
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy
              ? "Seeding…"
              : plan?.ops.length
                ? `Seed ${plan.ops.length.toLocaleString()} cell${plan.ops.length === 1 ? "" : "s"}`
                : "Seed"}
          </button>
        </div>
      </div>
    </div>
  );
}
